<template>
  <b-overlay :show="loading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container class="py-5 custom-container container">
        <b-breadcrumb
          :items="items"
          class="pl-0 pt-3 mt-5 bg-transparent"
        ></b-breadcrumb>
        <b-row>
          <b-col>
            <h2 class="font-weight-bold file-view-title">{{ filename }}</h2>
          </b-col>

          <file-options
            v-if="file"
            :file="file"
            :sharedView="isSharedView"
            @showAlert="showAlert"
            @shareFile="shareFile"
            @deletionScheduled="fetchFile"
          ></file-options>
        </b-row>

        <!-- Deleting a file shared with a company starts a one-week notice
             period: the owner can undo it, the company can keep a copy. -->
        <b-alert
          v-if="pendingDeletionDate"
          show
          variant="warning"
          class="mt-3"
          data-testid="file-pending-deletion"
        >
          <div
            class="d-flex flex-column flex-md-row align-items-md-center justify-content-between"
          >
            <span v-if="isFileOwner">
              This file will be deleted on
              <strong>{{ pendingDeletionDate }}</strong
              >. The company it was shared with has been notified.
            </span>
            <span v-else>
              <template v-if="file.deletion_scope === 'account'">
                The tradie is deleting their account and all their uploaded
                files.
              </template>
              <template v-else>The tradie is deleting this file.</template>
              It will be removed on
              <strong>{{ pendingDeletionDate }}</strong
              >. Keep a copy now if you are required or permitted to retain it.
            </span>
            <b-button
              v-if="isFileOwner"
              size="sm"
              variant="outline-secondary"
              class="mt-2 mt-md-0 ml-md-3"
              :disabled="pendingActionLoading"
              @click="undoDeletion"
            >
              Undo delete
            </b-button>
            <b-button
              v-else-if="canKeepCopy"
              size="sm"
              variant="outline-secondary"
              class="mt-2 mt-md-0 ml-md-3"
              :disabled="pendingActionLoading || hasKeptCopy"
              @click="keepCopy"
            >
              {{ hasKeptCopy ? 'Already have a copy' : 'Keep a copy' }}
            </b-button>
          </div>
        </b-alert>

        <b-alert
          v-if="loadError"
          show
          variant="danger"
          class="mt-3"
          data-testid="file-view-load-error"
        >
          {{ loadError }}
        </b-alert>

        <b-alert v-if="isSharedView" show class="mt-3 read-only-banner">
          <b-icon-eye class="align-middle mr-1" />
          <span class="align-middle">
            Read-only view - no changes can be made from here.
          </span>
        </b-alert>
        <b-row class="mx-0 mt-4">
          <b-col
            sm="12"
            lg="8"
            md="7"
            class="d-flex flex-column file-view-main-col"
            ><b-row
              class="flex-row flex-nowrap overflow-auto file-view-thumb-row"
              v-if="file"
            >
              <b-col
                sm="6"
                lg="5"
                class="pl-0 file-view-thumb-col"
                v-for="file in file.files"
                :key="file.id"
              >
                <!--
                  Thumbnails use a plain <b-img>, not <b-img-lazy>: a file only
                  ever has a handful of attachments and they sit at the top of
                  the page, so lazy-loading buys nothing. <b-img-lazy> also
                  broke here — its IntersectionObserver starts observing while
                  the route ("fade-in-up", a 400px translate) and page
                  ("bounce") enter transitions are still running, so the
                  thumbnail gets read as off-screen, never shown, and stays a
                  blank box until the user interacts. The preview dialog below
                  already uses a plain <b-img> for the same reason.
                -->
                <b-card
                  no-body
                  class="files"
                  @click="displayPreviewDialog(file)"
                >
                  <SpreadsheetThumbPreview
                    v-if="isSpreadsheetPreview(file)"
                    :file="file"
                  />
                  <b-img
                    v-else
                    v-bind="mainProps"
                    class="image"
                    :class="file.mime_type === 'image/jpeg' ? 'object-fit' : ''"
                    :src="thumbSrc(file)"
                    @error="onImageError"
                  ></b-img> </b-card></b-col
            ></b-row>
            <b-row class="flex-grow-1" v-if="file"
              ><b-col cols="12" class="mb-0 d-flex flex-column">
                <b-card class="mt-2 file-details flex-grow-1">
                  <h5 class="mb-2 font-weight-bold">File Details</h5>
                  <b-card-body class="pl-0 pt-0 align-items-center">
                    <b-row v-if="attributionSamePerson" class="mb-2">
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        Created &amp; owned by
                      </b-col>
                      <b-col cols="5" sm="5" md="8" lg="8" class="gray--2">{{
                        ownedByLabel
                      }}</b-col>
                    </b-row>
                    <template v-else>
                      <b-row class="mb-2">
                        <b-col
                          cols="7"
                          sm="7"
                          md="4"
                          lg="4"
                          class="font-weight-bold gray--2"
                        >
                          Created by
                        </b-col>
                        <b-col cols="5" sm="5" md="8" lg="8" class="gray--2">{{
                          createdByLabel
                        }}</b-col>
                      </b-row>
                      <b-row class="mb-2">
                        <b-col
                          cols="7"
                          sm="7"
                          md="4"
                          lg="4"
                          class="font-weight-bold gray--2"
                        >
                          Owned by
                        </b-col>
                        <b-col cols="5" sm="5" md="8" lg="8" class="gray--2">{{
                          ownedByLabel
                        }}</b-col>
                      </b-row>
                    </template>

                    <b-row v-if="file.user && file.user.email" class="mb-2">
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        Email
                      </b-col>
                      <b-col cols="5" sm="5" md="8" lg="8" class="gray--2">{{
                        file.user.email
                      }}</b-col>
                    </b-row>

                    <b-row class="mb-2">
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        Visibility
                      </b-col>
                      <b-col cols="5" sm="5" md="8" lg="8" class="gray--2">{{
                        fileVisibilityDisplay
                      }}</b-col>
                    </b-row>

                    <b-row
                      v-show="isFileShareable && !isBusinessAdminUser"
                      class="mb-2"
                    >
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        Shared to
                      </b-col>
                      <b-col cols="5" sm="5" md="8" lg="8" class="gray--2">
                        <b-button
                          v-if="fileSharesCount"
                          variant="link"
                          class="btn text-dark text-decoration-none btn-link p-0 h-auto"
                          @click="shareFile"
                        >
                          <span class="mr-2 align-middle">
                            <b-icon
                              scale="1"
                              icon="eye"
                              aria-hidden="true"
                            ></b-icon>
                          </span>
                          {{ fileSharesCount + ' people' }}
                        </b-button>
                        <span v-else class="text-muted">
                          <b-icon
                            scale="1"
                            icon="eye-slash"
                            aria-hidden="true"
                            class="mr-1"
                          ></b-icon>
                          None
                        </span>
                      </b-col>
                    </b-row>

                    <b-row v-if="fileStatusLabel === 'Rejected'" class="mb-2">
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        Reason
                      </b-col>
                      <b-col cols="5" sm="5" md="8" lg="8" class="gray--2">
                        <span
                          v-if="
                            file.file_rejects && file.file_rejects.length > 0
                          "
                          >{{ file.file_rejects[0].reason }}</span
                        >
                      </b-col>
                    </b-row>

                    <b-row class="mb-2">
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        Uploaded
                      </b-col>
                      <b-col cols="5" sm="5" md="8" lg="8" class="gray--2">{{
                        fileUploadedRelative
                      }}</b-col>
                    </b-row>

                    <b-row
                      class="mb-2"
                      v-for="info in visibleInfoTypes"
                      :key="info.id"
                    >
                      <b-col
                        cols="7"
                        sm="7"
                        md="4"
                        lg="4"
                        class="font-weight-bold gray--2"
                      >
                        {{ info.label }} </b-col
                      ><b-col cols="5" sm="5" md="8" lg="8" class="gray--2">{{
                        info.field_type === 'date'
                          ? parseDate(info.value)
                          : info.value
                      }}</b-col></b-row
                    ></b-card-body
                  >
                </b-card></b-col
              >
            </b-row>
          </b-col>

          <b-col
            v-if="file"
            sm="12"
            lg="4"
            md="5"
            class="upload-details file-view-sidebar-col"
          >
            <file-upload-details
              :file="file"
              :show-licence-providers-link="false"
              @openShareFile="shareFile"
            >
              <template v-if="showApprovalSection" #approval>
                <template v-if="isBusinessAdminUser">
                  <b-row class="font-weight-bold">Company Approval</b-row>
                  <b-row class="mt-1 mb-2">
                    <company-approval-badge
                      :status="file.company_approval"
                      :can-edit="false"
                    />
                  </b-row>

                  <b-row
                    v-if="approvalByLabel || approvalAtLabel"
                    class="small text-secondary mb-1"
                  >
                    <span class="font-weight-bold mr-1">
                      {{ approvalActionLabel }}
                    </span>
                    <span v-if="approvalByLabel">by {{ approvalByLabel }}</span>
                    <span v-if="approvalAtLabel" class="ml-1">
                      on {{ approvalAtLabel }}
                    </span>
                  </b-row>

                  <b-row
                    v-if="
                      file.company_approval === 'rejected' &&
                      file.company_approval_reason
                    "
                    class="small text-secondary mb-2"
                  >
                    <span class="font-weight-bold mr-1">Reason:</span>
                    "{{ file.company_approval_reason }}"
                  </b-row>

                  <b-row
                    v-if="
                      file.company_approval === 'approved' &&
                      file.company_approval_reason
                    "
                    class="small text-secondary mb-2"
                  >
                    <span class="font-weight-bold mr-1">Comment:</span>
                    "{{ file.company_approval_reason }}"
                  </b-row>

                  <b-row
                    v-if="
                      file.company_approval === 'pending' &&
                      file.company_approval_reason
                    "
                    class="small text-secondary mb-2"
                  >
                    <span class="font-weight-bold mr-1">Comment:</span>
                    "{{ file.company_approval_reason }}"
                  </b-row>

                  <b-row
                    v-if="
                      file.company_approval === 'not_applicable' &&
                      file.company_approval_reason
                    "
                    class="small text-secondary mb-2"
                  >
                    <span class="font-weight-bold mr-1">Comment:</span>
                    "{{ file.company_approval_reason }}"
                  </b-row>

                  <b-row class="mt-2 approval-actions-row">
                    <div
                      v-if="canManageApproval"
                      class="d-flex approval-actions-buttons"
                      style="gap: 8px"
                    >
                      <b-button
                        variant="success"
                        size="sm"
                        class="approval-action-btn"
                        :disabled="
                          !!approvalAction ||
                          file.company_approval === 'approved'
                        "
                        @click="onApproveClick"
                      >
                        <b-spinner
                          v-if="approvalAction === 'approve'"
                          small
                          label="Working"
                        />
                        <span v-else>Approve</span>
                      </b-button>
                      <b-button
                        variant="warning"
                        size="sm"
                        class="approval-action-btn"
                        :disabled="
                          !!approvalAction ||
                          file.company_approval === 'pending'
                        "
                        @click="onPendingClick"
                      >
                        <b-spinner
                          v-if="approvalAction === 'pending'"
                          small
                          label="Working"
                        />
                        <span v-else>Pending</span>
                      </b-button>
                      <b-button
                        variant="danger"
                        size="sm"
                        class="approval-action-btn"
                        :disabled="!!approvalAction"
                        @click="onRejectClick"
                      >
                        <b-spinner
                          v-if="approvalAction === 'reject'"
                          small
                          label="Working"
                        />
                        <span v-else>Reject</span>
                      </b-button>
                      <b-button
                        variant="secondary"
                        size="sm"
                        class="approval-action-btn"
                        :disabled="
                          !!approvalAction ||
                          file.company_approval === 'not_applicable'
                        "
                        @click="onNotApplicableClick"
                      >
                        <b-spinner
                          v-if="approvalAction === 'not_applicable'"
                          small
                          label="Working"
                        />
                        <span v-else>Archive</span>
                      </b-button>
                    </div>
                  </b-row>
                </template>

                <!-- Tradie's own profile: one badge per company+project that
                     has acted on this file (scalar company_approval is null
                     here; per-company-per-project statuses come from
                     companyApprovals) — a company can now approve on one
                     project and archive on another, so each is shown
                     separately rather than blended into one status. -->
                <template v-else>
                  <b-row class="font-weight-bold">Company Approval</b-row>
                  <div
                    v-for="entry in companyApprovals"
                    :key="`${entry.company_id}-${
                      entry.project_id != null ? entry.project_id : 'general'
                    }`"
                    class="company-approval-entry mt-2"
                  >
                    <b-row class="align-items-center mb-1">
                      <span class="font-weight-bold mr-2">
                        {{ entry.company_name }}
                        <span
                          v-if="entry.project_name"
                          class="font-weight-normal text-secondary"
                        >
                          — {{ entry.project_name }}
                        </span>
                      </span>
                      <company-approval-badge
                        :status="entry.status"
                        :reason="entry.reason || ''"
                        :set-date="entry.at || ''"
                        :can-edit="false"
                      />
                    </b-row>

                    <b-row
                      v-if="entry.by && entry.by.name"
                      class="small text-secondary mb-1"
                    >
                      <span class="font-weight-bold mr-1">
                        {{ entryActionLabel(entry) }}
                      </span>
                      <span>by {{ entry.by.name }}</span>
                      <span v-if="formatApprovalDate(entry.at)" class="ml-1">
                        on {{ formatApprovalDate(entry.at) }}
                      </span>
                    </b-row>

                    <b-row
                      v-if="entry.reason"
                      class="small text-secondary mb-2"
                    >
                      <span class="font-weight-bold mr-1">
                        {{
                          entry.status === 'rejected' ? 'Reason:' : 'Comment:'
                        }}
                      </span>
                      "{{ entry.reason }}"
                    </b-row>
                  </div>
                </template>
              </template>
            </file-upload-details>
          </b-col>
        </b-row>
        <!-- <b-alert
          class="custom-alert"
          :show="dismissCountDown"
          dismissible
          fade
          variant="primary"
          @dismiss-count-down="countDownChanged"
          >File has been {{ alertMsg }}.</b-alert
        > -->
        <share-file-modal
          :showShareFiles="showShareFiles"
          :selectedFiles="checkedFiles"
          :fileShares="fileShares"
          @closeShareFileModal="showShareFiles = false"
          @fileShareResponsePrompt="shareFileResponsePrompt"
          @reloadFileList="reloadFileShareState"
        />

        <ApproveFileModal
          :showModal="isConfirmingApprove"
          :fileName="filename"
          @close="onCloseApprove"
          @confirm="onConfirmApprove"
        />

        <PendingFileModal
          :showModal="isConfirmingPending"
          :fileName="filename"
          @close="onClosePending"
          @confirm="onConfirmPending"
        />

        <NotApplicableFileModal
          :showModal="isConfirmingNotApplicable"
          :fileName="filename"
          @close="onCloseNotApplicable"
          @confirm="onConfirmNotApplicable"
        />

        <RejectFileModal
          :showModal="isRejectingFile"
          :fileName="filename"
          @close="onCloseReject"
          @confirm="onConfirmReject"
        />

        <b-alert
          class="custom-alert"
          :show="dismissCountDown"
          :class="{
            'bg-danger border-danger': alertType === 'danger',
          }"
          dismissible
          fade
          :variant="alertType"
          @dismiss-count-down="countDownChanged"
        >
          {{ alertMsg }}
        </b-alert>
        <b-modal
          v-if="clickedFile"
          @click:outside="hidePreviewDialog"
          @keydown.esc="hidePreviewDialog"
          v-model="previewDialog.show"
          body-bg-variant="transparent"
          :body-class="
            previewDialog.type === 'application/pdf' ? 'p-0' : 'px-0'
          "
          hide-footer
          hide-header
          centered
          :size="previewDialog.type === 'application/pdf' ? 'xl' : 'lg'"
        >
          <div class="preview-toolbar">
            <b-button
              size="sm"
              variant="light"
              class="preview-download-btn"
              :disabled="previewDownloading"
              @click="downloadPreviewFile"
            >
              <b-icon icon="download" class="mr-1" />
              {{ previewDownloading ? 'Downloading…' : 'Download' }}
            </b-button>
          </div>
          <div
            v-if="previewDialog.type === 'application/pdf'"
            class="pdf-resize-wrapper"
          >
            <iframe
              :src="clickedFile"
              class="pdf-preview"
              frameborder="0"
            ></iframe>
          </div>
          <!-- CSV/XLSX: real content, fetched through the API and parsed
               client-side (plain text split for CSV, SheetJS for XLSX) —
               falls back to the file-type icon below if the fetch/parse
               fails for any reason (empty file, unsupported workbook,
               etc). -->
          <div v-else-if="previewIsSpreadsheet" class="csv-preview-wrapper">
            <div v-if="spreadsheetPreviewLoading" class="text-center py-5">
              <digger-loader />
            </div>
            <div
              v-else-if="
                spreadsheetPreviewRows && spreadsheetPreviewRows.length
              "
              class="csv-preview-table-scroll"
            >
              <table class="csv-preview-table">
                <tbody>
                  <tr
                    v-for="(row, rIndex) in spreadsheetPreviewRows"
                    :key="rIndex"
                    :class="{ 'csv-preview-header': rIndex === 0 }"
                  >
                    <td v-for="(cell, cIndex) in row" :key="cIndex">
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="non-previewable-preview">
              <b-img :src="previewDocIcon" class="non-previewable-icon" />
              <p class="mt-3 mb-0">This file type can't be previewed.</p>
            </div>
          </div>
          <b-img
            v-else
            class="preview"
            :src="clickedFile"
            @error="onImageError"
          ></b-img>
        </b-modal>
        <div>
          <b-button
            variant="primary"
            class="mt-3 d-inline-flex align-items-center"
            size="lg"
            :to="backDestination"
          >
            Back
          </b-button>
        </div>
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import FileOptions from '@/components/modals/FileOptions'
import { parseServerDate } from '@/utils/dateHelper'
import FileUploadDetails from '@/components/cards/FileUploadDetails'
import ShareFileModal from '@/components/modals/ShareFileModal'
import CompanyApprovalBadge from '@/components/badges/CompanyApprovalBadge.vue'
import RejectFileModal from '@/components/modals/RejectFileModal.vue'
import ApproveFileModal from '@/components/modals/ApproveFileModal.vue'
import PendingFileModal from '@/components/modals/PendingFileModal.vue'
import NotApplicableFileModal from '@/components/modals/NotApplicableFileModal.vue'
import {
  getCreatedByLabel,
  getOwnedByLabel,
  isSameCreatorAndOwner,
  formatFileVisibilityDisplay,
  visibleFileInformationTypes,
} from '@/utils/fileLabels'
import {
  nonImageDocIcon,
  isCsvFile as isCsvFileType,
  isXlsxFile as isXlsxFileType,
  parseCsvPreviewRows,
  parseXlsxPreviewRows,
} from '@/utils/fileTypeIcons'
import SpreadsheetThumbPreview from '@/components/files/SpreadsheetThumbPreview'

// The file view's own sub-pages (edit / upload success / new upload). Arriving
// from one of these is a step *back out* of that flow, not a fresh origin, so
// the recorded "where the user came from" must be left untouched — otherwise
// view -> edit -> Back -> view -> Back bounces between edit and view forever.
//
// template.form-page (TemplateFormPage.vue, e.g. "Passport") is the actual
// route for picking a specific document type off TemplatesPage — the create
// flow doesn't stay on file.new-page once a template's chosen (see
// TemplatesPage's goToTemplateForm()). Missing this here meant uploading a
// tradie's file from a project profile (Profile -> Templates -> Passport
// form -> upload -> file view) recorded the blank Passport form itself as
// the "came from" page, so Back went there instead of back to the profile.
// The two request.* equivalents cover the same picker inside the file
// *request* flow, for the same reason.
const FILE_VIEW_SUBROUTES = [
  'file.view-page',
  'file.edit-page',
  'file.new-page',
  'file.success-page',
  'template.form-page',
  'request.request-file-new-page',
  'request.request-file-template-form-page',
]

// Module-scoped so the real origin survives this component being destroyed
// while the user dips into the file's edit / success pages and comes back.
let lastFileViewOrigin = null

// Pulls the tradie (and project) out of a project-tradie or company-tradie
// profile path, or null for any other route. Used to tell whether a recorded
// origin is a *different* tradie's profile than the file's owner.
function parseTradieProfilePath(fullPath) {
  const path = String(fullPath || '').split(/[?#]/)[0]
  const project = path.match(/^\/projects\/([^/]+)\/tradie\/([^/]+)\/?$/)
  if (project && project[1] !== 'shared') {
    return { projectId: project[1], tradieId: project[2] }
  }
  const tradie = path.match(/^\/tradies\/([^/]+)\/?$/)
  if (tradie) return { projectId: null, tradieId: tradie[1] }
  return null
}

// Strips characters illegal in filenames and collapses whitespace out
// entirely (not just to a dash) so name/document segments below join
// cleanly on "_" — e.g. "White Card" -> "WhiteCard".
function sanitiseFilenameSegment(value) {
  return String(value || '')
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '')
}

// "Surname_FirstName" from the tradie's single full_name field (there's no
// separate first/last name column) — the last word is treated as the
// surname, everything before it as the given name(s). Empty when the file
// has no owner on record.
function tradieFilenameNamePart(user) {
  const fullName = user && user.full_name ? String(user.full_name).trim() : ''
  if (!fullName) return ''
  const words = fullName.split(/\s+/)
  const surname = sanitiseFilenameSegment(words.pop())
  const firstName = sanitiseFilenameSegment(words.join(' '))
  if (!surname && !firstName) return ''
  return firstName ? `${surname}_${firstName}` : surname
}

// Every Course / Training file carries the same generic `name` ("Course /
// Training") no matter which course was actually uploaded — the entered
// course name is what actually distinguishes them. The API only exposes
// that value baked into `display_name` ("Course / Training: Working With
// Power Tools" — see File::displayName()), so pull it back out by stripping
// the "<name>: " prefix rather than duplicating the file_information_types
// lookup client-side. Falls back to the plain name for every other file
// type, or if display_name doesn't have the expected shape.
function fileDocumentLabel(file) {
  const name = file && file.name ? String(file.name).trim() : ''
  const displayName =
    file && file.display_name ? String(file.display_name).trim() : ''
  if (name && displayName.startsWith(`${name}:`)) {
    const value = displayName.slice(name.length + 1).trim()
    if (value) return value
  }
  return name
}

export default {
  components: {
    FileOptions,
    FileUploadDetails,
    ShareFileModal,
    CompanyApprovalBadge,
    RejectFileModal,
    ApproveFileModal,
    PendingFileModal,
    NotApplicableFileModal,
    SpreadsheetThumbPreview,
  },

  // Record the exact in-app route the user arrived from so "Back" returns
  // there — a project's Expiring Files list, a tradie profile, search
  // results — with its query/filters intact. Without this, a file opened
  // from anywhere that doesn't tag a ?from= (e.g. a project's Expiring
  // Files) fell through to the business-admin localStorage guess in
  // backDestination(), which rebuilds a tradie route from the *last*
  // tradie_id/project_id written by a tradie-profile visit and lands on an
  // unrelated profile. Arrivals from the file view's own sub-pages
  // (FILE_VIEW_SUBROUTES) keep the previously recorded origin so the
  // view <-> edit round trip doesn't bounce. beforeRouteUpdate covers
  // navigating straight from one file to another (guard is not re-run on
  // component reuse).
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from && from.name && !FILE_VIEW_SUBROUTES.includes(from.name)) {
        lastFileViewOrigin = from.fullPath
      }
      vm.originRoute = lastFileViewOrigin
    })
  },

  beforeRouteUpdate(to, from, next) {
    if (from && from.name && !FILE_VIEW_SUBROUTES.includes(from.name)) {
      lastFileViewOrigin = from.fullPath
    }
    this.originRoute = lastFileViewOrigin
    next()
  },

  data() {
    return {
      loading: false,
      loadError: '',
      // Set by the route guards above; null on a direct link / hard reload /
      // cross-tab open, where backDestination() falls back to ?from= or the
      // tradie-context guess.
      originRoute: null,
      mainProps: {
        center: true,
      },
      showShareFiles: false,
      checkedFiles: [],
      fileShares: [],
      dismissCountDown: 0,
      pendingActionLoading: false,
      keptCopyLocally: false,
      alertMsg: '',
      alertType: 'primary',
      previewDialog: {
        show: false,
        type: null,
        file: null,
      },
      clickedFile: null,
      previewDownloading: false,
      spreadsheetPreviewRows: null,
      spreadsheetPreviewLoading: false,

      isRejectingFile: false,
      isConfirmingApprove: false,
      isConfirmingPending: false,
      isConfirmingNotApplicable: false,
      approvalAction: null,
    }
  },

  computed: {
    ...mapState({
      file: (state) => state.file.file,
    }),
    ...mapGetters('file', ['file']),
    ...mapGetters('auth', ['user', 'hasPermission']),
    // Drops the retired "Consent" information type (see fileLabels).
    visibleInfoTypes() {
      return visibleFileInformationTypes(
        this.file && this.file.file_information_types,
      )
    },
    filename() {
      return this.file ? this.file.name : ''
    },
    isSharedView() {
      return (
        this.$route && this.$route.query && this.$route.query.shared === '1'
      )
    },
    items() {
      return [
        // {
        //   text: 'Files',
        //   to: '/file',
        // },
        // {
        //   text: this.filename,
        // },
      ]
    },
    ownedByLabel() {
      return this.file ? getOwnedByLabel(this.file) : ''
    },
    createdByLabel() {
      return this.file
        ? getCreatedByLabel(this.file, (id) => this.resolveCreatorName(id))
        : ''
    },
    attributionSamePerson() {
      return this.file
        ? isSameCreatorAndOwner(this.file, (id) => this.resolveCreatorName(id))
        : false
    },
    fileVisibilityDisplay() {
      return this.file ? formatFileVisibilityDisplay(this.file) : ''
    },
    isBusinessAdminUser() {
      return this.user ? this.user.user_type === 'business_admin' : false
    },
    isFileOwner() {
      return !!(this.file && this.file.user && this.file.user.mine)
    },
    pendingDeletionDate() {
      if (!this.file || !this.file.deletion_scheduled_at) return ''
      const d = parseServerDate(this.file.deletion_scheduled_at)
      return d
        ? d.toLocaleDateString('en-AU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : ''
    },
    // The API reports a prior copy on reload; keptCopyLocally covers the
    // moment right after clicking, before the file is refetched.
    hasKeptCopy() {
      return !!(
        this.keptCopyLocally ||
        (this.file && this.file.viewer_has_retained_copy)
      )
    },
    canKeepCopy() {
      return (
        this.isBusinessAdminUser &&
        !this.isSharedView &&
        // The copy is a new file in the admin's own library, so uploading is
        // required as well as downloading (mirrors FileController@retainCopy).
        (this.hasPermission('tradies.files.download') ||
          this.hasPermission('projects.tradie.files.download')) &&
        this.hasPermission('files.upload')
      )
    },
    fileSharesCount() {
      return (
        (this.file && this.file.file_shares && this.file.file_shares.length) ||
        0
      )
    },
    fileStatusLabel() {
      if (!this.file || !this.file.status) return ''
      return this.file.status
        .trim()
        .toLowerCase()
        .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase()))
    },
    isFileShareable() {
      if (!this.file) return false
      return (
        this.file.status === 'valid' ||
        this.file.status === 'not started' ||
        this.file.status === 'expiring soon'
      )
    },
    fileUploadedRelative() {
      if (!this.file || !this.file.updated_at) return ''
      return this.getTimeDiff(this.file.updated_at)
    },
    getTradieId() {
      return localStorage.getItem('tradie_id')
    },
    canShareFile() {
      return !this.isBusinessAdminUser || this.hasPermission('files.share')
    },

    canManageApproval() {
      if (!this.user || this.user.user_type !== 'business_admin') return false
      if (
        !this.hasPermission('files.approve') &&
        !this.hasPermission('projects.tradie.files.approve')
      ) {
        return false
      }
      if (this.isSharedView) return false
      if (!this.file) return false
      // The backend authorises approval per tradie-owned file (can_manage_approval).
      if (this.file.can_manage_approval === false) return false
      return this.isTradieOwnedFile
    },

    isTradieOwnedFile() {
      const owner = this.file && this.file.user
      return !!(owner && owner.user_type === 'tradie')
    },

    companyApprovals() {
      const list = this.file && this.file.company_approvals
      return Array.isArray(list) ? list : []
    },

    isProjectTradieContext() {
      return (
        this.$route &&
        this.$route.query &&
        this.$route.query.from === 'project-tradie'
      )
    },

    /**
     * The project this file is being viewed under (set on the route by
     * ViewFileModal.vue when opened from the Projects tab). company_approval
     * and the four approve/pending/reject/archive actions below are scoped to
     * this exact project when present; without it (a bare GET /file/:id —
     * roster-level profile, notifications, a direct link) they roll up across
     * every project this company has an opinion on for the file instead.
     */
    routeProjectId() {
      const raw =
        this.$route && this.$route.query && this.$route.query.project_id
      if (raw == null || raw === '') return null
      const n = Number(raw)
      return Number.isFinite(n) ? n : null
    },

    showApprovalSection() {
      if (!this.file) return false
      // Business admins only manage company approval when they reached the file
      // by viewing a tradie on the project tab.
      if (this.isBusinessAdminUser)
        return this.isTradieOwnedFile && this.isProjectTradieContext
      // Tradie: only show company approvals for public files — private files
      // owned by the tradie never expose company decisions to them.
      if (this.file.visibility !== 'public') return false
      return this.companyApprovals.length > 0
    },

    approvalByLabel() {
      const by = this.file && this.file.company_approval_by
      if (!by) return ''
      return by.name || ''
    },

    approvalAtLabel() {
      return this.formatApprovalDate(this.file && this.file.company_approval_at)
    },

    approvalActionLabel() {
      return this.statusActionLabel(this.file && this.file.company_approval)
    },

    fileOwnerId() {
      const owner = this.file && this.file.user
      return owner && owner.id != null ? String(owner.id) : null
    },

    // The recorded origin, parsed, when it is a tradie profile that doesn't
    // belong to the open file's owner; null otherwise (including while the
    // file hasn't loaded, so it can't wrongly discard a good origin).
    staleOriginProfile() {
      if (!this.originRoute || !this.isTradieOwnedFile || !this.fileOwnerId) {
        return null
      }
      const origin = parseTradieProfilePath(this.originRoute)
      return origin && origin.tradieId !== this.fileOwnerId ? origin : null
    },

    // The project-tradie profile route to return to when the recorded origin
    // is the same tradie's company-level profile (no project) but the live
    // tradie_id/project_id context points at that tradie within a project;
    // null otherwise.
    projectProfileForOrigin() {
      if (!this.isBusinessAdminUser) return null
      const origin = parseTradieProfilePath(this.originRoute)
      if (!origin || origin.projectId) return null

      const projectId = localStorage.getItem('project_id')
      if (!projectId || projectId === 'undefined') return null
      if (localStorage.getItem('tradie_id') !== origin.tradieId) return null

      return {
        name: 'projects.view-tradie',
        params: { id: projectId, tradieId: origin.tradieId },
      }
    },

    backDestination() {
      const q = this.$route.query || {}

      if (q.shared === '1' && q.shareId && q.tradieId) {
        return {
          name: 'projects.shared-view-tradie',
          params: {
            shareId: String(q.shareId),
            tradieId: String(q.tradieId),
          },
        }
      }

      // Prefer the exact page the user came from within the app (captured by
      // the route guards) over the ?from= hints and the tradie-context guess
      // below — it preserves the origin list's filters and never lands on a
      // stale tradie profile.
      // ...unless that origin is another tradie's profile than the file's
      // owner (a stale origin kept across the edit/success round trip) —
      // then it's wrong, so fall through and rebuild from the owner.
      const staleOrigin = this.staleOriginProfile
      if (this.originRoute && !staleOrigin) {
        // The recorded origin can be a plain /tradies/:id profile left over
        // from an earlier visit, while this file was just uploaded from that
        // same tradie's *project* profile (the form replaces itself with this
        // page, so the origin is never re-recorded). The active upload
        // context says which one the user is really in.
        const projectProfile = this.projectProfileForOrigin
        if (projectProfile) return projectProfile
        return this.originRoute
      }

      if (q.from === 'file-list') {
        return { name: 'file.list-page' }
      }

      if (q.from === 'notification') {
        return { name: 'home.welcome-page' }
      }

      // Opened via "View full file" from the Home page's Expiring Soon list.
      // That list lives on the home page itself (no dedicated route), so
      // returning there is the closest we can get back to it.
      if (q.from === 'expiring-home') {
        return { name: 'home.welcome-page' }
      }

      // tradies.view-tradie is business_admin-only; sending a tradie there
      // gets blocked by the route guard and breaks back navigation.
      // The file's owner is the source of truth for whose profile to return
      // to; localStorage tradie_id/project_id only reflect the last profile
      // visited, which may not be this file's tradie.
      const tradieId =
        (this.isTradieOwnedFile && this.fileOwnerId) || this.getTradieId
      const projectId =
        (staleOrigin && staleOrigin.projectId) ||
        localStorage.getItem('project_id')
      if (this.isBusinessAdminUser && tradieId) {
        if (projectId && projectId !== 'undefined') {
          return {
            name: 'projects.view-tradie',
            params: { id: projectId, tradieId },
          }
        }
        return {
          name: 'tradies.view-tradie',
          params: { tradieId },
        }
      }

      // Supervisor tradies viewing another tradie's files within a project.
      const isSupervisorTradie =
        this.user && this.user.user_type === 'tradie' && this.user.is_supervisor
      if (
        isSupervisorTradie &&
        tradieId &&
        projectId &&
        projectId !== 'undefined'
      ) {
        return {
          name: 'projects.view-tradie',
          params: { id: projectId, tradieId },
        }
      }

      // No accessible tradie context (e.g. tradie user / direct link / stale
      // storage): fall back to where they came from.
      return { name: 'file.list-page' }
    },

    // CSV/XLSX can't be shown in the PDF-iframe branch or the plain <img>
    // branch — there's no browser-native inline view for either, and no
    // thumb_url to fall back to (see thumbSrc()). Both get a real content
    // table instead (loadSpreadsheetPreview) — previewDocIcon is just the
    // fallback within that branch if the fetch/parse fails.
    previewIsSpreadsheet() {
      const file = this.previewDialog.file
      return !!file && (isCsvFileType(file) || isXlsxFileType(file))
    },
    previewDocIcon() {
      return nonImageDocIcon(this.previewDialog.file)
    },
  },

  methods: {
    ...mapActions({
      getFile: 'file/getFile',
      setCompanyApproval: 'companyFile/setCompanyApproval',
    }),

    handleApprovalError(err) {
      const status = err && err.response && err.response.status
      const payload = err && err.response && err.response.data
      let message
      if (status === 403) {
        message = "You don't have permission to approve files for this tradie"
      } else if (status === 404) {
        message = 'File not found.'
      } else {
        message =
          (payload && payload.message) ||
          (err && err.message) ||
          'Could not update approval.'
      }
      this.alertType = 'danger'
      this.alertMsg = message
      this.dismissCountDown = 5
    },

    applyApprovalUpdate(updated) {
      if (!updated) return
      this.$store.commit('file/mergeFileCompanyApproval', updated)
      this.$store.commit('company/updateFileCompanyApproval', updated)
    },

    statusActionLabel(status) {
      if (status === 'approved') return 'Approved'
      if (status === 'rejected') return 'Rejected'
      if (status === 'not_applicable') return 'Archived'
      return 'Pending'
    },

    entryActionLabel(entry) {
      return this.statusActionLabel(entry && entry.status)
    },

    formatApprovalDate(at) {
      if (!at) return ''
      const d = new Date(at)
      if (Number.isNaN(d.getTime())) return ''
      const day = String(d.getDate()).padStart(2, '0')
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ]
      return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`
    },

    onApproveClick() {
      this.isConfirmingApprove = true
    },

    onCloseApprove() {
      this.isConfirmingApprove = false
    },

    async onConfirmApprove({ reason, done } = {}) {
      if (
        !this.file ||
        !this.canManageApproval ||
        this.routeProjectId == null
      ) {
        if (typeof done === 'function')
          done(new Error('Missing file, permission, or project'))
        return
      }
      this.approvalAction = 'approve'
      try {
        const payload = {
          fileId: this.file.id,
          status: 'approved',
          projectId: this.routeProjectId,
        }
        if (reason) payload.reason = reason
        const updated = await this.setCompanyApproval(payload)
        this.applyApprovalUpdate(updated)
        this.alertType = 'primary'
        this.alertMsg = 'File approved.'
        this.dismissCountDown = 5
        if (typeof done === 'function') done()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status !== 422) {
          this.handleApprovalError(err)
        }
        if (typeof done === 'function') done(err)
      } finally {
        this.approvalAction = null
      }
    },

    onPendingClick() {
      this.isConfirmingPending = true
    },

    onClosePending() {
      this.isConfirmingPending = false
    },

    async onConfirmPending({ reason, done } = {}) {
      if (
        !this.file ||
        !this.canManageApproval ||
        this.routeProjectId == null
      ) {
        if (typeof done === 'function')
          done(new Error('Missing file, permission, or project'))
        return
      }
      this.approvalAction = 'pending'
      try {
        const payload = {
          fileId: this.file.id,
          status: 'pending',
          projectId: this.routeProjectId,
        }
        if (reason) payload.reason = reason
        const updated = await this.setCompanyApproval(payload)
        this.applyApprovalUpdate(updated)
        this.alertType = 'primary'
        this.alertMsg = 'File set to pending.'
        this.dismissCountDown = 5
        if (typeof done === 'function') done()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status !== 422) {
          this.handleApprovalError(err)
        }
        if (typeof done === 'function') done(err)
      } finally {
        this.approvalAction = null
      }
    },

    onNotApplicableClick() {
      this.isConfirmingNotApplicable = true
    },

    onCloseNotApplicable() {
      this.isConfirmingNotApplicable = false
    },

    async onConfirmNotApplicable({ reason, done } = {}) {
      if (
        !this.file ||
        !this.canManageApproval ||
        this.routeProjectId == null
      ) {
        if (typeof done === 'function')
          done(new Error('Missing file, permission, or project'))
        return
      }
      this.approvalAction = 'not_applicable'
      try {
        const payload = {
          fileId: this.file.id,
          status: 'not_applicable',
          projectId: this.routeProjectId,
        }
        if (reason) payload.reason = reason
        const updated = await this.setCompanyApproval(payload)
        this.applyApprovalUpdate(updated)
        this.alertType = 'primary'
        this.alertMsg = 'File archived.'
        this.dismissCountDown = 5
        if (typeof done === 'function') done()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status !== 422) {
          this.handleApprovalError(err)
        }
        if (typeof done === 'function') done(err)
      } finally {
        this.approvalAction = null
      }
    },

    onRejectClick() {
      this.isRejectingFile = true
    },

    onCloseReject() {
      this.isRejectingFile = false
    },

    async onConfirmReject({ reason, done }) {
      if (!this.file || this.routeProjectId == null) {
        done(new Error('Missing file or project'))
        return
      }
      this.approvalAction = 'reject'
      try {
        const updated = await this.setCompanyApproval({
          fileId: this.file.id,
          status: 'rejected',
          reason,
          projectId: this.routeProjectId,
        })
        this.applyApprovalUpdate(updated)
        this.alertType = 'primary'
        this.alertMsg = 'File rejected.'
        this.dismissCountDown = 5
        done()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status !== 422) {
          this.handleApprovalError(err)
        }
        done(err)
      } finally {
        this.approvalAction = null
      }
    },

    resolveCreatorName(id) {
      if (this.user && this.user.id === id) {
        return (this.user.full_name || this.user.email || '').trim() || null
      }
      const u =
        (this.file && this.file.user) ||
        (this.file && this.file.file && this.file.file.user)
      if (u && Number(u.id) === Number(id)) {
        return (u.full_name || u.email || '').trim() || null
      }
      return null
    },

    // Owner changes their mind during the notice period.
    async undoDeletion() {
      this.pendingActionLoading = true
      try {
        await this.$api.post(`file/${this.file.id}/cancel-deletion`)
        await this.fetchFile()
        this.alertType = 'primary'
        this.alertMsg = 'Deletion cancelled. Your file has been kept.'
        this.dismissCountDown = 5
      } catch (err) {
        this.alertType = 'danger'
        this.alertMsg = 'Could not cancel the deletion. Please try again.'
        this.dismissCountDown = 5
      } finally {
        this.pendingActionLoading = false
      }
    },

    // Company keeps its own private copy before the tradie's file is removed.
    async keepCopy() {
      if (this.hasKeptCopy) return
      this.pendingActionLoading = true
      try {
        await this.$api.post(`file/${this.file.id}/retain-copy`)
        this.keptCopyLocally = true
        this.alertType = 'primary'
        this.alertMsg = 'A copy has been saved to your company files.'
        this.dismissCountDown = 5
      } catch (err) {
        this.alertType = 'danger'
        this.alertMsg = 'Could not keep a copy. Please try again.'
        this.dismissCountDown = 5
      } finally {
        this.pendingActionLoading = false
      }
    },

    async fetchFile() {
      this.loading = true
      this.loadError = ''
      const id = this.$route.params.id
      const payload =
        this.routeProjectId != null
          ? { id, projectId: this.routeProjectId }
          : id
      try {
        await this.getFile(payload)
      } catch (err) {
        // Clear any previously viewed file so it doesn't keep rendering
        // behind the error banner when navigating file -> file (beforeRouteUpdate
        // reuses this component instead of remounting it).
        this.$store.commit('file/clearFile')
        const status = err && err.response && err.response.status
        if (status === 403) {
          this.loadError = "You don't have access to this file."
        } else if (status === 404) {
          this.loadError = 'This file could not be found.'
        } else {
          this.loadError = 'Could not load this file.'
        }
      } finally {
        this.loading = false
      }
    },
    shareFile() {
      if (!this.canShareFile) return

      this.fileShares = this.file.file_shares
      this.showShareFiles = true
    },
    // Revoking a share (the modal's "x" on a "Currently shared with" tag)
    // doesn't close the modal, so the modal's fileShares prop must be
    // refreshed here too, or reopening it later still shows the revoked email.
    async reloadFileShareState() {
      await this.fetchFile()
      this.fileShares = this.file.file_shares || []
    },
    shareFileResponsePrompt(res) {
      if (res.success) {
        this.alertType = 'primary'

        this.alertMsg = 'File shared successfully!'
        this.dismissCountDown = 15
        this.fetchFile()

        return
      } else {
        this.alertType = 'danger'

        if (res.status === 402) {
          this.alertMsg = 'File shared unsuccessful!'
        } else {
          this.alertMsg =
            'Something went wrong while submitting the share file email!'
        }
      }

      this.dismissCountDown = 5
    },
    showAlert(action) {
      this.dismissCountDown = 1
      // this.alertMsg = action
      this.alertMsg = `File has been ${action}.`
      if (action === 'deleted') {
        const projectId = localStorage.getItem('project_id')
        const tradieId = localStorage.getItem('tradie_id')

        if (projectId && tradieId)
          if (projectId === 'undefined')
            this.$router.push({
              name: 'tradies.view-tradie',
              params: { tradieId: tradieId },
            })
          else
            this.$router.push({
              name: 'projects.view-tradie',
              params: { id: projectId, tradieId: tradieId },
            })
        else
          this.$router.replace({
            name: 'file.list-page',
          })
      }
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    // CSV/XLSX never get a real thumb_url — spatie/medialibrary can
    // rasterize an image or a PDF's first page, but there's no conversion
    // path for a spreadsheet, so thumb_url is missing/broken for these.
    // Render the bundled icon directly instead of letting the <img> fail
    // and fall back to onImageError's generic icon.
    thumbSrc(file) {
      return nonImageDocIcon(file) || file.thumb_url || file.url
    },

    displayPreviewDialog(file) {
      this.previewDialog.file = file
      this.previewDialog.type = file.mime_type
      this.clickedFile =
        file.mime_type === 'application/pdf'
          ? file.url
          : file.url || file.thumb_url
      this.togglePreviewDialog()

      if (isCsvFileType(file) || isXlsxFileType(file)) {
        this.loadSpreadsheetPreview(file)
      }
    },

    // Template-callable wrapper — Vue 2 templates can't call a plain
    // imported function directly, only data/computed/methods.
    isSpreadsheetPreview(file) {
      return isCsvFileType(file) || isXlsxFileType(file)
    },

    // The preview <img>/<iframe> src is the raw media URL, whose last path
    // segment is the storage hash — so the browser's own "Save as" offers
    // that hash as the filename. Pull the bytes through the authenticated
    // API instead (same media/{id}/content endpoint the spreadsheet preview
    // uses — it sidesteps the bucket's missing CORS policy) and save them
    // under the file's own name.
    async downloadPreviewFile() {
      const attachment = this.previewDialog.file
      if (!attachment || !attachment.id || this.previewDownloading) return
      this.previewDownloading = true
      let objectUrl = null
      try {
        const { data: blob } = await this.$api.get(
          `media/${attachment.id}/content`,
          { responseType: 'blob' },
        )
        objectUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = this.previewDownloadName(attachment)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (e) {
        this.alertMsg = 'Could not download this file.'
        this.alertType = 'danger'
        this.dismissCountDown = 5
      } finally {
        if (objectUrl) window.URL.revokeObjectURL(objectUrl)
        this.previewDownloading = false
      }
    },

    // "Reed_Cooper_WhiteCard.pdf" — the tradie's Surname_FirstName plus the
    // file's document label, so the name survives once the file is saved
    // outside the app's own folder structure. Falls back to the document
    // type alone when the file has no owner on record. Course / Training
    // files use the entered course name (fileDocumentLabel()) instead of the
    // generic "Course / Training" name shared by every course. The
    // attachment's real extension comes from its stored file_name; multiple
    // attachments on one record are numbered so the saved files don't
    // collide.
    previewDownloadName(attachment) {
      const raw = fileDocumentLabel(this.file)
      const docSegment = sanitiseFilenameSegment(raw) || 'file'
      const namePart = tradieFilenameNamePart(this.file && this.file.user)
      const base = namePart ? `${namePart}_${docSegment}` : docSegment
      const source = (attachment && attachment.file_name) || ''
      const dot = source.lastIndexOf('.')
      let ext = dot >= 0 ? source.slice(dot).toLowerCase() : ''
      const mimeExt = {
        'application/pdf': ['.pdf'],
        'image/png': ['.png'],
        'image/jpeg': ['.jpg', '.jpeg'],
      }[attachment && attachment.mime_type]
      if (mimeExt && !mimeExt.includes(ext)) ext = mimeExt[0]
      const list = (this.file && this.file.files) || []
      let suffix = ''
      if (list.length > 1) {
        const idx = list.findIndex((f) => f.id === attachment.id)
        if (idx > 0) suffix = ` (${idx + 1})`
      }
      return `${base}${suffix}${ext}`
    },

    // Fetched through the authenticated $api client against this app's own
    // API (media/{id}/content) rather than the file's raw S3 url directly —
    // the S3/Spaces bucket has no CORS policy allowing browser fetch() reads
    // (confirmed: the request itself returns 200, but the browser blocks JS
    // from reading the body — "No 'Access-Control-Allow-Origin' header").
    // Going through the API sidesteps that entirely, since it already has
    // CORS configured for this frontend.
    //
    // CSV is plain text (parseCsvPreviewRows: a best-effort naive comma
    // split, not a real parser — fine for a preview, the actual import
    // elsewhere in the app parses properly server-side) and safely
    // truncatable, so it's fetched capped to a small max_bytes. XLSX is a
    // zip container — truncating it mid-file corrupts the archive, so it's
    // fetched in full (no max_bytes) and parsed with SheetJS
    // (parseXlsxPreviewRows). Anything that goes wrong here (network, empty
    // file, unsupported workbook) just leaves spreadsheetPreviewRows empty,
    // which the template falls back to the icon for.
    async loadSpreadsheetPreview(file) {
      this.spreadsheetPreviewRows = null
      this.spreadsheetPreviewLoading = true

      try {
        if (isXlsxFileType(file)) {
          const { data } = await this.$api.get(`media/${file.id}/content`, {
            responseType: 'arraybuffer',
          })
          this.spreadsheetPreviewRows = parseXlsxPreviewRows(data)
        } else {
          const { data: text } = await this.$api.get(
            `media/${file.id}/content`,
            {
              responseType: 'text',
              // Only ever shows 15 rows — no need to transfer more than a
              // small multiple of that.
              params: { max_bytes: 16384 },
            },
          )
          this.spreadsheetPreviewRows = parseCsvPreviewRows(text)
        }
      } catch (err) {
        this.spreadsheetPreviewRows = null
      } finally {
        this.spreadsheetPreviewLoading = false
      }
    },

    hidePreviewDialog() {
      this.togglePreviewDialog()

      this.previewDialog.file = null
      this.spreadsheetPreviewRows = null
      this.spreadsheetPreviewLoading = false
    },

    togglePreviewDialog() {
      this.previewDialog.show = !this.previewDialog.show
    },
    onImageError(e) {
      e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' fill='%230066FF' class='bi bi-file-earmark' viewBox='0 0 16 16'%3E%3Cpath d='M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z'/%3E%3C/svg%3E`
    },
  },

  async created() {
    await this.fetchFile().then(() => {
      if (this.file) {
        this.checkedFiles.push(this.file.id)
      }
    })
  },
}
</script>
<style lang="scss" scoped>
.file-details {
  height: auto;
}

.card {
  border-radius: 8px;
  opacity: 1;
  margin: 0;
}

.preview-container {
  height: 192px;
  flex-wrap: nowrap;

  @media (max-width: 767px) {
    width: auto;
  }
}

.files {
  height: 192px;
}

.image {
  cursor: pointer;
  height: 100%;
  object-fit: contain;

  @media (min-width: 767px) {
    width: 100%;
  }
}

.preview {
  display: block;
  max-width: 100%;
  max-height: 80vh;
  width: auto;
  height: auto;
  margin: 0 auto;
}

// Anchored to the modal content so it floats over any preview type
// (image / PDF iframe / spreadsheet table).
.preview-toolbar {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 5;
}

.preview-download-btn {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.non-previewable-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1rem;
}

.non-previewable-icon {
  width: 96px;
  height: auto;
}

.csv-preview-wrapper {
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
}

.csv-preview-table-scroll {
  max-width: 100%;
  max-height: 70vh;
  overflow: auto;
}

.csv-preview-table {
  border-collapse: collapse;
  font-size: 0.85rem;
  white-space: nowrap;

  td {
    border: 1px solid #e1e6ea;
    padding: 0.4rem 0.6rem;
  }
}

.csv-preview-header {
  font-weight: 700;
  background: #f5f7f9;
}

.pdf-resize-wrapper {
  resize: both;
  overflow: hidden;
  width: 100%;
  height: 80vh;
  min-width: 300px;
  min-height: 200px;
}

.pdf-preview {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
}

.list {
  justify-content: space-around;
}

.upload-details {
  padding-right: 0;

  @media (max-width: 767px) {
    padding-left: 0;
    padding-top: 1.5rem;
  }
}

.approval-actions-row {
  min-height: 28px;
}

.approval-action-btn {
  padding: 4px 12px;
  line-height: 1.2;
  min-height: 0;
}

@media (max-width: 575px) {
  .approval-actions-buttons {
    flex-wrap: wrap;
    row-gap: 8px;
  }

  // Below 576px the sm/md/lg column widths above don't apply, and were
  // left as auto-sized flex items instead of stacking full width. This
  // override only exists inside this media query, so nothing at 576px
  // and up is affected.
  .file-view-main-col,
  .file-view-sidebar-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .file-view-thumb-col {
    flex: 0 0 50% !important;
    max-width: 50% !important;
  }

  // The thumbnail strip scrolled horizontally because it never wrapped.
  // Wrapping it (2 per row, per .file-view-thumb-col above) keeps every
  // thumbnail on screen without a horizontal swipe.
  .file-view-thumb-row {
    flex-wrap: wrap !important;
    overflow: visible !important;
  }

  .files {
    height: 140px;
  }

  .file-view-title {
    font-size: 1.35rem;
  }

  .file-details h5 {
    font-size: 0.95rem;
  }

  .file-details .gray--2 {
    font-size: 0.8rem;
    // Long, unbroken values (emails, URLs, filenames) were forced into a
    // cramped col-5 and broke mid-word. Let them wrap on any boundary.
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  // Below sm the detail rows keep Bootstrap's col-7 / col-5 split, leaving
  // the value less than half the width. Stack label above value, each on
  // its own full-width line, so nothing is squeezed.
  .file-details .card-body .row.mb-2 > [class*='col-'] {
    flex: 0 0 100%;
    max-width: 100%;
  }

  .file-details .card-body .row.mb-2 .font-weight-bold.gray--2 {
    margin-bottom: 0.1rem;
  }
}
</style>
